import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dataExportGetExportJobStatus } from '../../../api';

export type TrackedJob = {
	jobId: string;
	label: string;
	state: 'Pending' | 'Processing' | 'Completed' | 'Failed';
	downloadUrls?: Record<string, string> | null;
	queuePosition?: number | null;
	errorMessage?: string | null;
	startedAt?: string | null;
};

function JobPoller({
	jobId,
	onUpdate,
}: {
	jobId: string;
	onUpdate: (jobId: string, update: Partial<TrackedJob>) => void;
}) {
	const { data } = useQuery({
		queryKey: ['export-job-status', jobId],
		queryFn: () => dataExportGetExportJobStatus({ jobId }),
		refetchInterval: (q) => {
			const state = q.state.data?.state;
			if (state === 'Completed' || state === 'Failed') {
				return false;
			}
			return 5000;
		},
	});

	useEffect(() => {
		if (!data) {
			return;
		}
		onUpdate(jobId, {
			state: data.state as TrackedJob['state'],
			downloadUrls: data.downloadUrls,
			queuePosition: data.queuePosition,
			errorMessage: data.errorMessage,
			startedAt: data.startedAt,
		});
	}, [jobId, onUpdate, data]);

	return null;
}

type ExportJobsBarProps = {
	jobs: TrackedJob[];
	onUpdate: (jobId: string, update: Partial<TrackedJob>) => void;
	onDismiss: (jobId: string) => void;
};

function SpinnerIcon() {
	return (
		<svg className="animate-spin h-4 w-4 text-smartfield flex-shrink-0" fill="none" viewBox="0 0 24 24">
			<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
			<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
		</svg>
	);
}

export function ExportJobsBar({ jobs, onUpdate, onDismiss }: ExportJobsBarProps) {
	if (jobs.length === 0) {
		return null;
	}

	return (
		<div className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white shadow-lg">
			{jobs.map(job => (
				<div key={job.jobId}>
					<JobPoller
						jobId={job.jobId}
						onUpdate={onUpdate}
					/>
					<div className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 last:border-b-0 text-sm">
						{(job.state === 'Pending' || job.state === 'Processing') && <SpinnerIcon />}
						{job.state === 'Completed' && (
							<svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
							</svg>
						)}
						{job.state === 'Failed' && (
							<svg className="h-4 w-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						)}

						<span className="text-gray-700 truncate flex-1">{job.label}</span>

						{job.state === 'Pending' && job.queuePosition != null && (
							<span className="text-xs text-gray-400 flex-shrink-0">Queue #{job.queuePosition}</span>
						)}
						{job.state === 'Pending' && job.queuePosition == null && (
							<span className="text-xs text-gray-400 flex-shrink-0">Pending…</span>
						)}
						{job.state === 'Processing' && (
							<span className="text-xs text-gray-400 flex-shrink-0">Processing…</span>
						)}
						{job.state === 'Completed' && job.downloadUrls && Object.keys(job.downloadUrls).length > 0 && (
							<span className="flex gap-2 flex-shrink-0">
								{Object.entries(job.downloadUrls).map(([kind, url]) => (
									<a
										key={kind}
										href={url}
										target="_blank"
										rel="noopener noreferrer"
										className="text-xs font-medium text-smartfield hover:underline"
									>
										{kind.toUpperCase()}
									</a>
								))}
							</span>
						)}
						{job.state === 'Failed' && (
							<span className="text-xs text-red-500 flex-shrink-0 max-w-xs truncate">
								{job.errorMessage ?? 'Export failed'}
							</span>
						)}

						{(job.state === 'Completed' || job.state === 'Failed') && (
							<button
								onClick={() => onDismiss(job.jobId)}
								className="text-gray-400 hover:text-gray-600 flex-shrink-0"
								aria-label="Dismiss"
							>
								<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						)}
					</div>
				</div>
			))}
		</div>
	);
}
